import type { Critter, CritterType } from "../creatures/dna";
import { getCreatureSprite, getCreatureVector } from "./assets";
import { blitSprite, parseSprite, snap, type Palette, type Sprite } from "./blit";
import { drawVectorSprite, vectorContentBottom } from "./vectorPixel";

/**
 * Five+ distinct shore critters — different silhouettes, not recolors.
 * Shared only by outline/face conventions; bodies are unique shapes.
 */

/** 0 ROUND — chubby pebble beast */
const ROUND = parseSprite([
  "................................",
  "................................",
  "..........OOOOOOOOOO............",
  "........OOHHHHHHHHDDOO..........",
  ".......OHHHLLLLLLLLLDDO.........",
  "......OHHHLLLLLLLLLLLDDO........",
  "......OHHKKKKKKKKKKKKDDO........",
  "......OHHKKPKKKKKPKKKDDO........",
  "......OHHKKKKKKKKKKKKDDO........",
  "......OLLLKKKKNNKKKKDDDO........",
  ".....OOLLLLKKKKKKKKKDDDOO.......",
  "....OOMMMMLLLLUUUUULLDDMMOO.....",
  "...OOMMMMLLLLUUIIUULLDDMMMOO....",
  "...OMMMMLLLLUUIIIIUULLDMMMMO....",
  "...OMMMMLLLLUUUIIUUULLDMMMMO....",
  "...OODDDLLLLUUUUUUULLLDDDDOO....",
  "....OODDDDDDDDDDDDDDDDDDDOO.....",
  ".....OOMMMOOOOOOOOOOMMMOO.......",
  "......OMMMO........OMMO.........",
  "......ODDDO........ODDO.........",
  "......OOOOO........OOOO.........",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

/** 1 HOP — tall-eared lanky hopper */
const HOP = parseSprite([
  "................................",
  "......OO..............OO........",
  ".....OLLO............OLLO.......",
  ".....OLAO............OALO.......",
  ".....OLAO............OALO.......",
  ".....OLLO............OLLO.......",
  "......OOO....OOOO....OOO........",
  "........OOOOHHHHDDOOOO..........",
  ".......OHHHHHLLLLLDDDO..........",
  ".......OHHHKKKKKKKKDDO..........",
  ".......OHHHKKPKKPKKDDO..........",
  ".......OLLLKKKKNNKKDDO..........",
  "......OOLLLLKKKKKKKDDDO.........",
  "......OMMMMLLUUUULLDDMO.........",
  "......OMMMMLLUIIUULLDMO.........",
  "......OODDDLLUIIUULLDDO.........",
  ".......OODDDDDDDDDDDOO..........",
  "........OOMOO....OOMOO..........",
  ".........OMO......OMO...........",
  ".........ODO......ODO...........",
  ".........OOO......OOO...........",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

/** 2 HORN — wide horned tank */
const HORN = parseSprite([
  "................................",
  "....AA..................AA......",
  "...ALLA................ALLA.....",
  "...ALOLA..............ALOLA.....",
  "....AOOA....OOOOOO....AOOA......",
  "......OOOOOHHHHHHHDDOOOO........",
  ".....OHHHHHLLLLLLLLLDDDO........",
  "....OHHHHHLLLLLLLLLLLDDDO.......",
  "....OHHHKKKKKKKKKKKKKKDDO.......",
  "....OHHHKKPKKKKKKKPKKKDDO.......",
  "....OHHHKKKKKKKKKKKKKKDDO.......",
  "....OLLLKKKKNNKKKKKKKDDDO.......",
  "...OOLLLLLLLLLLLLLLLLLDDOO......",
  "..OOMMMMMMMMUUUUUUMMMMMMMOO.....",
  "..OMMMMMMMMUUIIIIUUMMMMMMMO.....",
  "..OMMMMMMMMUUUIIUUUMMMMMMMO.....",
  "..OODDDDDDDUUUUUUUDDDDDDDOO.....",
  "...OODDDDDDDDDDDDDDDDDDDOO......",
  "....OOMMMOOOOOOOOOOOMMMOO.......",
  ".....OMMO............OMMO.......",
  ".....ODDO............ODDO.......",
  ".....OOOO............OOOO.......",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

/** 3 KIT — pointed ears + long curling tail */
const KIT = parseSprite([
  "................................",
  "........O..............O........",
  ".......OAO............OAO.......",
  "......OALAO..........OALAO......",
  ".......OOO............OOO.......",
  "........OOOOOOOOOOOOOOO.........",
  ".......OHHHHHLLLLLLLDDO.........",
  "......OHHHHHLLLLLLLLLDDO........",
  "......OHHHKKKKKKKKKKKDDO........",
  "......OHHHKKPKKKKPKKKDDO........",
  "......OHHHKKKKKKKKKKKDDO........",
  "......OLLLKKKKNNKKKKDDDO........",
  ".....OOLLLKKKKKKKKKKDDDOO.......",
  ".....OMMMMLLUUUUUULLDDMO........",
  ".....OMMMMLLUUIIUULLDDMO........",
  ".....OODDDLLUUIIUULLDDOO...OO...",
  "......OODDDDDDDDDDDDOO...OMMO...",
  ".......OOMOO....OOMOO...OMMO....",
  "........OMO......ODO...ODDO.....",
  "........ODO......OOO...OOOO.....",
  "........OOO.....................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

/** 4 FINN — upright aquatic with dorsal crest */
const FINN = parseSprite([
  "................................",
  "..............AA................",
  ".............ALLA...............",
  "............ALLLLA..............",
  "...........OHLLLLDO.............",
  "..........OHHLLLLDDO............",
  "........OOOHHLLLLDDOOO..........",
  ".......OHHHHHLLLLLDDDO..........",
  "......OHHHKKKKKKKKKDDO..........",
  "......OHHHKKPKKKPKKDDO..........",
  "......OHHHKKKKKKKKKDDO..........",
  "......OLLLKKKKNNKKDDDO..........",
  ".....OOLLLLLLLLLLLLLDOO.........",
  "....OOMMMMUUUUUUUUMMMMOO........",
  "....OMMMMUUIIIIIIUUMMMMO........",
  "....OMMMMUUUUIIUUUUMMMMO........",
  "....OODDDUUUUUUUUUDDDDOO........",
  ".....OODDDDDDDDDDDDDDOO.........",
  "......OO..OMMMMMO..OO...........",
  "..........ODDDDDO...............",
  "..........OOOOOOO...............",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

/** 5 SPIKE — angular crystal critter */
const SPIKE = parseSprite([
  "................................",
  ".............A..A..A............",
  "............ALAALAALA...........",
  "...........ALOLAALOLA...........",
  "............AOOA.AOOA...........",
  ".........OOOOOOOOOOOOOO.........",
  "........OHHHHHLLLLLDDDOO........",
  ".......OHHHHHLLLLLLLLDDO........",
  ".......OHHHKKKKKKKKKKDDO........",
  ".......OHHHKKPKKKPKKKDDO........",
  ".......OHHHKKKKKKKKKKDDO........",
  ".......OLLLKKKKNNKKKKDDO........",
  "......OOLLLAAAAAAAAAADDOO.......",
  "......OMMMMLLUUUUULLDDMO........",
  "......OMMMMLLUIIUULLDDMO........",
  "......OODDDLLUIIUULLDDOO........",
  ".......OODDDDDDDDDDDOO..........",
  "........OAOO......OOAO..........",
  ".......OALAO......OALAO.........",
  "........AOO........OOA..........",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

const BODIES: Sprite[] = [ROUND, HOP, HORN, KIT, FINN, SPIKE];

const FACE_BELLY: Palette = {
  O: "#1a1020",
  K: "#f2c8a8",
  P: "#1a1020",
  N: "#c04058",
  U: "#fff8f0",
  I: "#79d6cb",
  S: "#ffe566",
  E: "#fff8e8",
  A: "#c09060",
};

const TYPE_COSTUME: Record<CritterType, Palette> = {
  normal: { D: "#6a4a28", M: "#a87840", L: "#d4a868", H: "#efd0a0", A: "#c09060" },
  steel: { D: "#3a4a5a", M: "#6a7a8a", L: "#a8b8c8", H: "#d0dce8", A: "#5a90b0" },
  psychic: { D: "#5a2870", M: "#9a50b8", L: "#c888e0", H: "#ecc8f8", A: "#e070c0" },
  electric: { D: "#8a7020", M: "#d0b038", L: "#f0d860", H: "#fff4a8", A: "#f09020" },
  ghost: { D: "#2a2048", M: "#5040a0", L: "#8070d0", H: "#b8a8f0", A: "#d070e8" },
  grass: { D: "#286030", M: "#48a050", L: "#78d070", H: "#b0f0a0", A: "#e0c040" },
};

const SHINY = parseSprite([
  "................................",
  "................S...............",
  "...............SSS..............",
  "................S...............",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
  "................................",
]);

/** Unified on-screen scale — PNG bake is already game-sized. */
export function critterScale(_size: number): number {
  return 1;
}

/** Drawn footprint for vector sprite (or chibi fallback). */
export function critterDrawSize(c: Critter): { w: number; h: number } {
  const vec = getCreatureVector(c.body);
  if (vec) return { w: vec.w, h: vec.h };
  const png = getCreatureSprite(c.body);
  if (png) return { w: png.width, h: png.height };
  return {
    w: CREATURE_SPRITE_SIZE,
    h: CREATURE_SPRITE_SIZE,
  };
}

export function drawCritterSprite(
  ctx: CanvasRenderingContext2D,
  c: Critter,
  opts?: { seatX?: number; seatY?: number },
): void {
  const vec = getCreatureVector(c.body);
  // Whole-pixel bob only — subpixel bob shimmers on NN upscale.
  const bob =
    opts?.seatX !== undefined ? 0 : Math.round(Math.sin(c.bob) * 1);
  const cx = opts?.seatX ?? c.x;
  const cy = opts?.seatY ?? c.y;

  ctx.save();
  ctx.globalAlpha = 1;
  ctx.imageSmoothingEnabled = false;

  if (vec) {
    // Facing is baked RIGHT in assets.ts (source art faces left).
    const x = snap(cx - vec.w / 2);
    const y = snap(cy - vec.h / 2 + bob);
    drawVectorSprite(ctx, vec, x, y);
    if (c.shiny) {
      blitSprite(
        ctx,
        SHINY,
        { S: "#ffe566" },
        x + Math.floor(vec.w / 2) - Math.floor(SHINY.w / 2),
        y - 1,
        1,
      );
    }
  } else {
    // Fallback while assets decode (or on load failure) — creatures always visible.
    const sprite = BODIES[c.body % BODIES.length]!;
    const palette: Palette = { ...FACE_BELLY, ...TYPE_COSTUME[c.type] };
    const x = snap(cx - sprite.w / 2);
    const y = snap(cy - sprite.h / 2 + bob);
    blitSprite(ctx, sprite, palette, x, y, 1);
    if (c.shiny) {
      blitSprite(ctx, SHINY, { S: "#ffe566" }, x, y - 1, 1);
    }
  }
  ctx.restore();
}

/** Bottom of the drawn sprite in world Y — label sits ~1–2px under the feet. */
export function critterLabelAnchor(c: Critter): { x: number; y: number } {
  const bob = Math.round(Math.sin(c.bob) * 1);
  const vec = getCreatureVector(c.body);
  const { h } = critterDrawSize(c);
  // Prefer opaque content bottom so empty bake rows don't open a gap.
  const feet = vec
    ? -Math.floor(h / 2) + vectorContentBottom(vec)
    : Math.ceil(h / 2);
  return {
    x: snap(c.x),
    y: snap(c.y + feet + bob + 1),
  };
}

/** Full clickable bounds (sprite + label strip). */
export function critterHitBox(c: Critter): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const { w, h } = critterDrawSize(c);
  return {
    x: snap(c.x - w / 2),
    y: snap(c.y - h / 2),
    w,
    // Include room for the tiny txid under the feet.
    h: h + 8,
  };
}

export const CREATURE_SPRITE_SIZE = 64;
export const CREATURE_BODY_COUNT = BODIES.length;
